/**
 * TÄLU · Ingredients renderer
 *
 * Lee landing/data/ingredients.json y renderiza la sección de ingredientes
 * en cualquier elemento <div data-ingredients-mount="raw|clear|premium"></div>.
 *
 * MIGRACIÓN A SHOPIFY: este script se reemplaza por un partial Liquid
 * `sections/ingredients.liquid` que itera `product.metafields.custom.ingredients`.
 * El HTML/CSS resultante es IDÉNTICO — sólo cambia la fuente de datos.
 *
 * Uso en HTML:
 *   <div data-ingredients-mount="raw"></div>
 *   <script src="../scripts/ingredients.js" defer></script>
 */
(function () {
  'use strict';

  const STYLE_ID = 'talu-ingredients-style';
  const CSS = `
    .ingredients-section{
      padding: 110px 0 90px;
    }
    .ingredients-section .ing-header{
      max-width: 720px;
      margin: 0 auto 50px;
      text-align: center;
      padding: 0 40px;
    }
    .ingredients-section .ing-eyebrow{
      font-size: 11px; letter-spacing: .32em; text-transform: uppercase;
      font-weight: 500;
      color: var(--gold-deep, #8a6a32);
    }
    .ingredients-section h2{
      font-family: "Cormorant Garamond", serif;
      font-weight: 400;
      font-size: clamp(34px, 4.4vw, 56px);
      letter-spacing: -.015em;
      margin: 12px 0 0;
      line-height: 1.05;
    }
    .ingredients-section h2 em{
      font-style: italic;
      color: var(--gold-deep, #8a6a32);
    }
    .ingredients-section .ing-lead{
      margin-top: 16px;
      font-family: "Cormorant Garamond", serif; font-style: italic;
      font-size: clamp(15px, 1.4vw, 18px);
      line-height: 1.55;
      opacity: .8;
    }

    .ing-grid{
      max-width: 1180px;
      margin: 0 auto;
      padding: 0 40px;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
    }
    @media (max-width: 900px){
      .ing-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }
    @media (max-width: 600px){
      .ing-grid{ grid-template-columns: 1fr; }
      .ing-card.is-featured{ grid-column: auto !important; }
    }
    .ing-card{
      padding: 22px 20px;
      border-radius: 14px;
      background: rgba(0,0,0,.025);
      border: 1px solid rgba(201,163,93,.22);
      display: flex; gap: 16px; align-items: flex-start;
      transition: transform .45s cubic-bezier(.2,.7,.2,1), border-color .35s ease, box-shadow .45s ease, background .35s ease;
      transform-style: preserve-3d;
      perspective: 900px;
      position: relative;
      will-change: transform;
      --mx: 0; --my: 0;
    }
    .ing-card.is-revealing{
      opacity: 0;
      transform: translateY(24px) scale(.96);
    }
    .ing-card.is-revealed{
      opacity: 1;
      transform: translateY(0) scale(1);
      transition: opacity .8s ease, transform .8s cubic-bezier(.2,.7,.2,1);
    }
    .ing-card:hover{
      transform: perspective(900px) rotateX(calc(var(--my) * -6deg)) rotateY(calc(var(--mx) * 6deg)) translateY(-4px) translateZ(8px);
      border-color: rgba(201,163,93,.65);
      box-shadow: 0 22px 42px -18px rgba(166,130,61,.45),
                  0 6px 14px -8px rgba(166,130,61,.25);
      background: rgba(201,163,93,.06);
    }
    .ing-card.is-featured{
      grid-column: 1 / -1;
      padding: 32px 30px;
      background:
        radial-gradient(ellipse 100% 70% at 0% 50%, rgba(201,163,93,.16), transparent 70%),
        rgba(0,0,0,.04);
      border-color: rgba(201,163,93,.5);
      box-shadow: 0 22px 44px -22px rgba(166,130,61,.4);
      animation: ing-glow-pulse 3.6s ease-in-out infinite;
    }
    @keyframes ing-glow-pulse{
      0%,100%{ box-shadow: 0 22px 44px -22px rgba(166,130,61,.4), 0 0 0 0 rgba(245,206,128,0); }
      50%{ box-shadow: 0 26px 52px -22px rgba(166,130,61,.55), 0 0 28px 2px rgba(245,206,128,.18); }
    }
    .ing-card.is-featured:hover{
      transform: perspective(900px) rotateX(calc(var(--my) * -3deg)) rotateY(calc(var(--mx) * 3deg)) translateY(-3px) translateZ(6px);
    }
    .ing-icon-box{
      width: 52px; height: 52px;
      flex-shrink: 0;
      display: grid; place-items: center;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(245,222,160,.4), rgba(201,163,93,.15));
      border: 1px solid rgba(201,163,93,.4);
      font-size: 28px;
      transition: transform .5s cubic-bezier(.2,.7,.2,1), box-shadow .4s ease, background .4s ease;
      animation: ing-float 4s ease-in-out infinite;
      transform-origin: center;
      will-change: transform;
    }
    @keyframes ing-float{
      0%,100%{ transform: translateY(0) rotate(0deg); }
      50%{ transform: translateY(-3px) rotate(.6deg); }
    }
    .ing-card:hover .ing-icon-box{
      transform: translateY(-6px) scale(1.12) rotate(-4deg) translateZ(20px);
      box-shadow: 0 14px 26px -10px rgba(166,130,61,.55);
      background: linear-gradient(135deg, rgba(255,210,138,.7), rgba(201,163,93,.35));
    }
    .ing-card.is-featured .ing-icon-box{
      width: 80px; height: 80px;
      font-size: 42px;
      background: linear-gradient(135deg, #ffd28a 0%, #f4be72 50%, #c9a35d 100%);
      box-shadow: 0 12px 26px -10px rgba(166,130,61,.5);
      animation: ing-float-featured 5s ease-in-out infinite;
    }
    @keyframes ing-float-featured{
      0%,100%{ transform: translateY(0) rotate(0deg) scale(1); }
      50%{ transform: translateY(-5px) rotate(1deg) scale(1.02); }
    }
    .ing-card.is-featured:hover .ing-icon-box{
      transform: translateY(-8px) scale(1.08) rotate(-3deg) translateZ(28px);
      box-shadow: 0 22px 42px -14px rgba(166,130,61,.7);
    }
    .ing-icon-box.is-multi{
      width: auto;
      min-width: 80px;
      padding: 0 14px;
      font-size: 32px;
      gap: 6px;
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
    .ing-card.is-featured .ing-icon-box.is-multi{
      width: auto;
      min-width: 220px;
      height: 80px;
      font-size: 38px;
      padding: 0 22px;
      gap: 12px;
      flex-direction: row;
      flex-shrink: 0;
    }
    /* Stagger float en los emojis individuales del multi-icon featured */
    .ing-card.is-featured .ing-icon-box.is-multi > .ing-emoji{
      display: inline-block;
      animation: ing-emoji-bob 2.6s ease-in-out infinite;
      transform-origin: center;
    }
    .ing-card.is-featured .ing-icon-box.is-multi > .ing-emoji:nth-child(1){ animation-delay: 0s; }
    .ing-card.is-featured .ing-icon-box.is-multi > .ing-emoji:nth-child(2){ animation-delay: .35s; }
    .ing-card.is-featured .ing-icon-box.is-multi > .ing-emoji:nth-child(3){ animation-delay: .7s; }
    @keyframes ing-emoji-bob{
      0%,100%{ transform: translateY(0) scale(1) rotate(0deg); }
      50%{ transform: translateY(-6px) scale(1.08) rotate(-4deg); }
    }
    .ing-card.is-featured:hover .ing-icon-box.is-multi > .ing-emoji{
      animation-duration: 1.4s;
    }
    @media (prefers-reduced-motion: reduce){
      .ing-icon-box, .ing-card.is-featured, .ing-card.is-featured .ing-icon-box.is-multi > .ing-emoji{
        animation: none !important;
      }
      .ing-card{ transition: border-color .2s ease, box-shadow .2s ease !important; }
    }
    /* Orphan centering: cuando hay 1 o 2 cards en la última fila, centrarlos */
    /* Caso 1 huérfano: simplemente moverlo a col 2 (centro) */
    .ing-grid.orphans-1 > .ing-card:last-child:not(.is-featured){
      grid-column: 2 / 3;
    }
    /* Caso 2 huérfanos: usar grid de 6 cols (cada card span 2) para que los 2 últimos queden en cols 2-3 y 4-5 = centrados con cols 1 y 6 vacías */
    .ing-grid.orphans-2{
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }
    .ing-grid.orphans-2 > .ing-card:not(.is-featured){
      grid-column: span 2;
    }
    .ing-grid.orphans-2 > .ing-card:nth-last-child(2):not(.is-featured){
      grid-column: 2 / span 2;
    }
    .ing-grid.orphans-2 > .ing-card:last-child:not(.is-featured){
      grid-column: 4 / span 2;
    }
    @media (max-width: 900px){
      .ing-grid.orphans-1 > .ing-card:last-child:not(.is-featured),
      .ing-grid.orphans-2 > .ing-card:not(.is-featured),
      .ing-grid.orphans-2 > .ing-card:nth-last-child(2):not(.is-featured),
      .ing-grid.orphans-2 > .ing-card:last-child:not(.is-featured){
        grid-column: auto !important;
        grid-column-start: auto !important;
      }
      .ing-grid.orphans-2{
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }
    }

    .ing-text{ flex: 1; min-width: 0; }
    .ing-name{
      font-family: "Cormorant Garamond", serif;
      font-weight: 500;
      font-size: 17px;
      margin: 0 0 4px;
      line-height: 1.2;
      display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
    }
    .ing-card.is-featured .ing-name{ font-size: 22px; }
    .ing-tag{
      display: inline-block;
      padding: 2px 9px;
      border-radius: 4px;
      background: linear-gradient(135deg, #ffd28a, #c9a35d);
      color: #1a1410;
      font-family: "Inter", sans-serif;
      font-size: 9px; letter-spacing: .18em; font-weight: 700;
    }
    .ing-desc{
      font-size: 13px; line-height: 1.55;
      opacity: .82;
      margin: 0;
    }
    .ing-card.is-featured .ing-desc{ font-size: 14px; }
    @media (max-width: 700px){
      .ingredients-section{ padding: 80px 0 60px; }
      .ing-card{ padding: 18px 16px; }
      .ing-card.is-featured{ flex-direction: row; align-items: flex-start; padding: 22px 18px; gap: 14px; }
      /* Force 1 column on mobile — override .orphans-2 / .orphans-1 from desktop rules */
      .ing-grid,
      .ing-grid.orphans-1,
      .ing-grid.orphans-2 {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
        padding: 0 20px;
      }
      .ing-grid > .ing-card,
      .ing-grid.orphans-1 > .ing-card,
      .ing-grid.orphans-2 > .ing-card,
      .ing-grid.orphans-2 > .ing-card:nth-last-child(2):not(.is-featured),
      .ing-grid.orphans-2 > .ing-card:last-child:not(.is-featured) {
        grid-column: 1 / -1 !important;
        grid-column-start: auto !important;
        grid-column-end: auto !important;
      }
      .ing-card.is-featured .ing-icon-box {
        width: 64px !important; height: 64px !important; font-size: 32px !important;
      }
      .ing-card.is-featured .ing-icon-box.is-multi {
        width: auto !important; min-width: 0 !important; max-width: 130px !important;
        height: 64px !important; font-size: 26px !important; padding: 0 10px !important;
        gap: 4px !important; flex-wrap: nowrap !important;
      }
      .ing-card.is-featured .ing-name { font-size: 18px !important; line-height: 1.2 !important; }
      .ing-card.is-featured .ing-desc { font-size: 13px !important; line-height: 1.5 !important; }
    }
  `;

  function injectStylesOnce(){
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function escapeHTML(s){
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  function isImageURL(s){
    return /^https?:\/\//.test(s) || /^\/?(assets|landing|img)/.test(s) || /\.(png|jpg|jpeg|webp|gif|svg)(\?|$)/i.test(s);
  }

  function renderIcon(item){
    const main = item.icon || '';
    const extras = Array.isArray(item.extra_icons) ? item.extra_icons : [];
    const allIcons = [main, ...extras].filter(Boolean);

    if (allIcons.length === 1) {
      const v = allIcons[0];
      if (isImageURL(v)) {
        return `<div class="ing-icon-box"><img src="${escapeHTML(v)}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:12px;"></div>`;
      }
      return `<div class="ing-icon-box"><span class="ing-emoji">${escapeHTML(v)}</span></div>`;
    }
    // Multi-icon: cada emoji se envuelve en .ing-emoji para stagger animation
    return `<div class="ing-icon-box is-multi">${allIcons.map(i => isImageURL(i)
      ? `<img src="${escapeHTML(i)}" alt="" style="width:48px;height:48px;border-radius:8px;">`
      : `<span class="ing-emoji">${escapeHTML(i)}</span>`
    ).join('')}</div>`;
  }

  function renderCard(item){
    const isFeatured = !!item.featured;
    const tag = item.tag ? `<span class="ing-tag">${escapeHTML(item.tag)}</span>` : '';
    return `
      <article class="ing-card${isFeatured ? ' is-featured' : ''}">
        ${renderIcon(item)}
        <div class="ing-text">
          <h3 class="ing-name">${escapeHTML(item.name)} ${tag}</h3>
          <p class="ing-desc">${escapeHTML(item.desc)}</p>
        </div>
      </article>`;
  }

  function renderSection(productKey, data){
    if (!data || !Array.isArray(data.items) || data.items.length === 0) return '';

    // Calcular huérfanos en la última fila para centrarlos via CSS
    // Featured ocupa toda la fila → solo cuentan los secundarios contra grid de 3 cols
    const items = data.items;
    const hasFeatured = items.some(it => it.featured);
    const secundarios = hasFeatured ? items.length - 1 : items.length;
    const orphans = secundarios % 3;
    const gridClass = orphans === 1 ? 'ing-grid orphans-1' : orphans === 2 ? 'ing-grid orphans-2' : 'ing-grid';

    return `
      <section class="ingredients-section" id="ingredientes" data-ingredients-rendered="${escapeHTML(productKey)}">
        <div class="ing-header">
          <span class="ing-eyebrow">${escapeHTML(data.title || 'Ingredientes reales')}</span>
          <h2>${escapeHTML(data.subtitle || '')}</h2>
          ${data.lead ? `<p class="ing-lead">${escapeHTML(data.lead)}</p>` : ''}
        </div>
        <div class="${gridClass}">
          ${data.items.map(renderCard).join('')}
        </div>
      </section>`;
  }

  async function loadAndRender(){
    const mounts = document.querySelectorAll('[data-ingredients-mount]');
    if (mounts.length === 0) return;

    let json;
    const url = window.TALU_INGREDIENTS_URL || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cdn/assets/ingredients.json' : 'ingredients.json');
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error('ingredients.json not found (HTTP ' + res.status + ')');
      json = await res.json();
    } catch (e) {
      console.warn('[ingredients] no se pudo cargar el JSON:', e);
      mounts.forEach(m => m.remove());
      return;
    }
    // Replace __TALU_SEBO_IMG__ placeholder with actual CDN URL
    const sebo = window.TALU_SEBO_IMG_URL;
    if (sebo && json && json.products) {
      Object.values(json.products).forEach(p => {
        (p.items || []).forEach(it => {
          if (it.icon === '__TALU_SEBO_IMG__') it.icon = sebo;
        });
      });
    }

    injectStylesOnce();

    mounts.forEach(mount => {
      const productKey = mount.dataset.ingredientsMount;
      const data = (json.products || {})[productKey];
      if (!data) {
        mount.remove();
        return;
      }
      mount.outerHTML = renderSection(productKey, data);
    });

    // Bind interactive 3D + reveal en los cards recién montados
    bindInteractive3D();

    // Post-render scroll: si la URL tiene #ingredientes, scroll a la sección recién montada
    if (window.location.hash === '#ingredientes') {
      requestAnimationFrame(() => {
        const target = document.getElementById('ingredientes');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  // Mouse-tilt + reveal-on-scroll
  function bindInteractive3D(){
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cards = document.querySelectorAll('.ingredients-section .ing-card');
    if (cards.length === 0) return;

    // Reveal stagger via IntersectionObserver
    cards.forEach((c, i) => c.classList.add('is-revealing'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting){
          const idx = Array.from(cards).indexOf(e.target);
          const delay = reduceMotion ? 0 : Math.min(idx * 80, 600);
          setTimeout(() => {
            e.target.classList.remove('is-revealing');
            e.target.classList.add('is-revealed');
          }, delay);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });
    cards.forEach((c) => io.observe(c));

    // Mouse-tilt: setea --mx / --my con la posición relativa del mouse en cada card
    if (reduceMotion) return;
    cards.forEach((card) => {
      let raf = null;
      const onMove = (ev) => {
        const r = card.getBoundingClientRect();
        const x = (ev.clientX - r.left) / r.width;  // 0..1
        const y = (ev.clientY - r.top) / r.height;  // 0..1
        const mx = (x - 0.5) * 2;  // -1..1
        const my = (y - 0.5) * 2;  // -1..1
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.setProperty('--mx', mx.toFixed(3));
          card.style.setProperty('--my', my.toFixed(3));
        });
      };
      const onLeave = () => {
        if (raf) cancelAnimationFrame(raf);
        card.style.setProperty('--mx', '0');
        card.style.setProperty('--my', '0');
      };
      card.addEventListener('mousemove', onMove);
      card.addEventListener('mouseleave', onLeave);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadAndRender);
  } else {
    loadAndRender();
  }
})();
